import {
  Caption,
  DataProps,
  DataSSR,
  Frame,
  HLSVideoTrack,
  MP4VideoTrack,
  PropsSSR,
  QualityType,
  Season,
  ThumbData,
  ThumbsData,
  VideoFileData,
  VideoFileType,
  VideoTrack,
} from './PlayerModels';
import { Content, fetchVideoData, sendVideoPlayBack } from '../../apiService';
import lscache from 'lscache';

export const formatPublicData = ({
  content,
  videoFileType,
}: DataSSR & {
  videoFileType: VideoFileType;
}): Omit<PropsSSR, 'videoFileType' | 'contentUrl'> => {
  return {
    id: content.id,
    titles: getTitles(videoFileType, content),
    cover: content.cover_image?.path,
  };
};

export const handleEpisodes = (data) => {
  const series: Season[] = [];
  data.forEach((item) => {
    series.push({
      id: item.season,
      episodes: item.episodes.map((ep) => {
        return {
          id: ep.episode,
          duration: ep.duration,
          cover_image: ep.cover_image.path,
          title: ep.title,
          subtitle: ep.sub_title,
          url: `/play/${ep.short_id}/p`,
          coming_soon: ep.flag.includes('coming_soon'),
        };
      }),
    });
  });
  return series;
};

export const getTitles = (videoFileType: DataProps['videoFileType'], content: Content): DataProps['titles'] => {
  const { title, original_name: title_en, sub_title, season, episode, type } = content;
  if (type === 'single_video' || videoFileType === 'trailer') {
    return {
      primary: persianDigits(`${title}`),
      secondary: title_en || '',
    };
  }

  const seasonIdentifier = season ? ` فصل ${season} ` : '';
  const episodeIdentifier = typeof episode === 'number' && episode > 0 ? `قسمت ${episode}` : '';
  const subtitle = sub_title ? ` : ${sub_title}` : '';
  const episodeSecondary = persianDigits(seasonIdentifier + episodeIdentifier + subtitle);

  return {
    primary: `${content.supper_title}`,
    secondary: episodeSecondary || '',
  };
};

export const getVideoFileId = (content: Pick<Content, 'id' | 'video_files'>, videoFileType: VideoFileType) => {
  const videoFileKey = videoFileType === 'trailer' ? 'trailers' : 'primary';
  return content?.video_files?.[videoFileKey]?.[0]?.video_file_id;
};

export const getVideoData = async ({
  tech = 'hls',
  contentId,
  videoFileId,
  handledByCaller,
}: {
  tech?: string | undefined;
  contentId: string;
  videoFileId: string | undefined;
  /**
   * Set by a caller that shows the refusal itself — shorts answer a paywall or an age
   * restriction with a notice over the player — so the global error snackbar stands down.
   */
  handledByCaller?: boolean;
}): Promise<VideoFileData> => {
  const techType = tech === 'mp4' ? 'mp4-url' : 'url';

  const { data } = await fetchVideoData(contentId, videoFileId, techType, { handledByCaller });
  return data;
};

export const persianDigits = (text?: string | number) => {
  if (!text) return '';
  const re = /(\d)/g;
  return String(text).replace(re, (d) => ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'][d]);
};

export const getMp4Quality = (currentMp4Src?: string): number => {
  if (!currentMp4Src) return 0;
  const url = currentMp4Src.split('.mp4')[0];
  const quality: Array<string> = [];
  // eslint-disable-next-line no-plusplus
  for (let i = url.length - 1; i >= 0; i--) {
    if (url[i] === '/') {
      break;
    } else if (url[i]) {
      quality.push(url[i]);
    }
  }
  quality.reverse();
  if (!quality.length) return 0;
  return Number(
    quality.reduce((accumulator, currentValue) => {
      return accumulator + currentValue;
    }),
  );
};

export const getMP4QualityType = (MP4Quality: string): QualityType => {
  switch (getMp4Quality(MP4Quality)) {
    case 1080:
      return 'FULL_HD';
    case 720:
      return 'HD';
    default:
      return 'SD';
  }
};

export const handleChangeMp4Quality = (sources: MP4VideoTrack[]) => {
  const data: MP4VideoTrack[] = [];

  // const highestQualityLevelOfAddiction: number = window.screen.height;
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < sources.length; i++) {
    // if (sources[i].quality <= highestQualityLevelOfAddiction) {
    sources[i].enable = true;
    data.push(sources[i]);
    // }
  }
  return data;
};

export const handleSources = (tech: string | undefined, contentId: string | undefined, videoData: VideoFileData) => {
  // let techSupport = tech;
  // if (videoData.url?.includes('gp00')) {
  //   techSupport = 'mp4';
  // }

  const data = {
    videos: [] as (VideoTrack | MP4VideoTrack)[],
    captions: [] as Caption[],
  };

  if (tech === 'mp4') {
    const localData: MP4VideoTrack[] = [];
    videoData.urls?.forEach((elem, index) => {
      elem.url &&
        localData.push({
          id: String(index),
          src: elem.url,
          type: 'video/mp4',
          enable: false,
          qualityType: getMP4QualityType(elem.url),
          quality: handleRangeQualities(elem.height || elem.width) as number,
          bitrate: elem.bit_rate,
        });
    });
    data.videos = normalizeQualities(localData);

    videoData.subtitle_tracks?.forEach((elem) => {
      data.captions.push({
        src: elem.url,
        language: elem.lang,
        srclang: elem.lang,
        label: elem.name,
        kind: 'subtitles',
        default: elem.default,
        mode: elem.default ? 'showing' : 'disabled',
      });
    });
  } else {
    data.videos.push({
      id: contentId,
      src: videoData.url,
      type: 'application/x-mpegURL',
    });
  }

  return data;
};

export const hasOldBrowser = (userAgent: string) => {
  const versionReg = /\b(Chromium\/[0-4][0-9])\b|(Chrome\/[0-4][0-9])\b|(Opera\/[0-4][0-9])\b|(Tizen [0,1,2])\b/gi;
  return versionReg.test(userAgent);
};
export const techSupport = (): string => {
  let tech = 'hls';
  const initialStoredTechConfig = getInitialStoredTechConfig();
  if (initialStoredTechConfig === 'mp4') tech = 'mp4';

  return tech;
};
// export const techSupport = (): string => {
//   const IS_IOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
//   let tech = 'mp4';
//   const initialStoredTechConfig = getInitialStoredTechConfig();
//   if ((initialStoredTechConfig === 'hls' && !hasOldBrowser(navigator.userAgent)) && (hlsSupported() || IS_IOS)) tech = 'hls';
//
//   return tech;
// };

export function hlsSupported(): boolean {
  const mediaSource: any = self.MediaSource || ((self as any).WebKitMediaSource as MediaSource);
  if (!mediaSource) {
    return false;
  }
  const sourceBuffer: typeof self.SourceBuffer = self.SourceBuffer || (self as any).WebKitSourceBuffer;
  const isTypeSupported =
    mediaSource &&
    typeof mediaSource.isTypeSupported === 'function' &&
    mediaSource.isTypeSupported('video/mp4; codecs="avc1.42E01E,mp4a.40.2"');
  const sourceBufferValidAPI =
    !sourceBuffer ||
    (sourceBuffer.prototype &&
      typeof sourceBuffer.prototype.appendBuffer === 'function' &&
      typeof sourceBuffer.prototype.remove === 'function');
  return !!isTypeSupported && !!sourceBufferValidAPI;
}

export const timeToSeconds = (time = '00:00:00'): number => {
  const [hours, minutes, seconds] = time.split(':');
  return Number(hours) * 60 * 60 + Number(minutes) * 60 + Number(seconds);
};

export const formatSeconds = (seconds: number) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);

  const formattedHours = String(hours).padStart(2, '0');
  const formattedMinutes = String(minutes).padStart(2, '0');
  const formattedSeconds = String(remainingSeconds).padStart(2, '0');

  return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
};

export const formatSecondsForReminder = (seconds: number) => {
  const absSeconds = Math.abs(seconds);
  const hours = Math.floor(absSeconds / 3600);
  const minutes = Math.floor((absSeconds % 3600) / 60);
  const remainingSeconds = Math.floor(absSeconds % 60);

  const formattedHours = String(hours).padStart(hours >= 10 ? 2 : hours < 10 && hours > 0 ? 1 : 0, '0');
  const formattedMinutes = String(minutes).padStart(2, '0');
  const formattedSeconds = String(remainingSeconds).padStart(2, '0');
  return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
};

export const updatePlayback = (id, file_id, current_time, video_url, is_short = false) => {
  sendVideoPlayBack({ id, file_id, current_time: formatSeconds(current_time), video_url, is_short }).catch((err) => {
    if (err?.response?.status === 409) window.location.replace(`/contents/${id}`);
  });
};

// videojs-contrib-ads renames the events of the video element around an ad break: they carry
// an 'ad' prefix while an ad plays and a 'content' prefix while the content comes back after
// one - the Vast plugin listens to 'advolumechange' for that reason. Anything that follows the
// volume has to listen to every name, or it stops updating from the moment an ad plays.
export const VOLUME_CHANGE_EVENTS = ['volumechange', 'advolumechange', 'contentvolumechange'];

// video.js announces the outcome of its own autoplay attempt on the player: 'autoplay-success'
// once playback has started, muted or not, and 'autoplay-failure' when nothing started at all.
// The mp4 player runs that attempt by hand (see Player.tsx and the Vast plugin's playLinearAd)
// and dispatches the same two names on the content <video>, so UnmuteNotice reads one signal in
// both players and can tell a mute the browser demanded from one the viewer chose.
export const AUTOPLAY_SUCCESS = 'autoplay-success';
export const AUTOPLAY_FAILURE = 'autoplay-failure';

const autoplayOutcomes = new WeakMap<HTMLVideoElement, boolean>();

export const announceAutoplay = (video: HTMLVideoElement | null, started: boolean) => {
  if (!video) return;
  // Recorded as well as dispatched. video.js lets a component that mounts late read the outcome
  // off the player itself, but the mp4 attempt can settle while the content element sits paused
  // behind a pre-roll playing on an overlay of its own - so `paused` answers nothing there, and
  // a listener alone would have nothing left to hear.
  autoplayOutcomes.set(video, started);
  video.dispatchEvent(new CustomEvent(started ? AUTOPLAY_SUCCESS : AUTOPLAY_FAILURE));
};

export const autoplayStarted = (video: HTMLVideoElement) => autoplayOutcomes.get(video) === true;

// Everything that can change whether the player is paused, under every name videojs-contrib-ads
// can give it. A play/pause button has to listen to all of them - and read player.paused()
// rather than trust the name of the event that woke it up - to stay in sync across an ad break.
export const PLAY_STATE_EVENTS = [
  'play',
  'adplay',
  'contentplay',
  'playing',
  'adplaying',
  'contentplaying',
  'pause',
  'adpause',
  'contentpause',
];

// The content reaching its end, under every name videojs-contrib-ads can give it. On content
// with an ad break the first `ended` is renamed to `contentended` while contrib-ads checks for
// a postroll, and the plain `ended` only follows once that check is done - so a listener bound
// to `ended` alone either hears the end late or, when a postroll swallows it, not at all.
// 'adended' is deliberately absent: that one is an ad finishing, not the content.
export const CONTENT_END_EVENTS = ['ended', 'contentended'];

// duration() keeps the value of the previous source until that source's own 'durationchange' -
// right after an ad break it is still the ad's - so a content position way past it is a stale
// duration, not a video that ended. Announcing an end there latched the play button on it for
// the rest of the session and its icon stopped following play and pause. Live is Infinity and
// never at its end.
export const isAtTrackEnd = (player) => {
  const duration = player.duration();
  const position = player.currentTime();
  return duration > 0 && isFinite(duration) && position >= duration - 0.5 && position <= duration + 1;
};

export const layersZIndex = {
  toolbar: {
    // The mp4 player plays its ads on an overlay <video> of its own instead of swapping the ad
    // source into the one element the way video.js does. It covers the content video, which is
    // statically positioned, and stays under every layer below - so the controls and the unmute
    // notice sit over an ad exactly as they sit over the content.
    adOverlay: 0,
    watermark: 1,
    bigBtn: 1,
    screen: 1,
    bar: 2,
    Panel: 2,
    unmuteNotice: 3,
    volume: {
      rangeWrapper: 4,
      rangeInput: 6,
      rangeUp: 5,
    },
  },
};

/**
 * @param height   video height (e.g. 1080)
 * @param bitrate  bits per second (e.g. 5000000)
 */
export const handleRangeQualities = (height: number): 240 | 360 | 480 | 720 | 1080 | 1081 | 1440 | 2160 | 4320 => {
  // const kbps = bitrate / 1000;

  if (height <= 240) {
    return 240;
  }

  if (height <= 360) {
    return 360;
  }

  if (height <= 480) {
    return 480;
  }

  if (height <= 720) {
    return 720;
  }
  return 1080;
  // if (height <= 1080 && kbps <= 6000) {
  //   return 1080;
  // }
  //
  // if (height <= 1081 && kbps > 6000) {
  //   return 1081;
  // }
  // return 1440;
};

type QualityWithBitrate = MP4VideoTrack | HLSVideoTrack;

export const normalizeQualities = <T extends QualityWithBitrate>(qualities: T[]): T[] => {
  const groups: { quality: number; sources: T[] }[] = [];
  const passthrough: T[] = [];

  qualities.forEach((source) => {
    const quality = Number(source.quality);

    // The HLS list is seeded with a non-numeric 'auto' entry. It is not a real rendition, so keep
    // it untouched and out of the numeric sort — Number('auto') is NaN, and a comparator that
    // returns NaN leaves the entry's position up to the engine rather than pinning it to the top.
    if (Number.isNaN(quality)) {
      passthrough.push(source);
      return;
    }

    const group = groups.find((item) => item.quality === quality);
    if (group) group.sources.push(source);
    else groups.push({ quality, sources: [source] });
  });

  const normalized: T[] = [];

  groups.forEach(({ quality, sources }) => {
    // Sort by bitrate so the pick is deterministic, rather than depending on the order the API
    // happened to return the sources in.
    const byBitrate = sources.slice().sort((a, b) => (Number(b.bitrate) || 0) - (Number(a.bitrate) || 0));

    // HQ has to be the higher-bitrate of the two 1080 streams.
    if (quality === 1080 && byBitrate.length > 1) {
      normalized.push({ ...byBitrate[0], quality: 1081 }, byBitrate[1]);
      return;
    }

    // Every other resolution keeps just its best stream. Extra streams above 1080 are folded in
    // here too, instead of being dropped on the floor as they were before.
    normalized.push(byBitrate[0]);
  });

  normalized.sort((a, b) => Number(b.quality) - Number(a.quality));

  return passthrough.concat(normalized);
};

export const handleSimplePlayerSubs = async (captions: Caption[]) => {
  const data: Caption[] = [];
  for (const track of captions) {
    const generatedUrl = await fetch(track.src).then((res) => res.text());
    const src = window.URL.createObjectURL(new Blob([generatedUrl], { type: 'text/vtt' }));
    data.push({
      src,
      language: track.language,
      srclang: track.srclang,
      label: findCaptionLabel(track.language),
      kind: track.kind,
      default: track.default,
      mode: track.mode,
    });
  }
  return data;
};

export const getInitialStoredTechConfig = () => {
  let initialStoredTechConfig = 'hls';
  if (typeof window !== 'undefined') {
    const config = lscache.get('player_config');
    if (config && config.tech) {
      initialStoredTechConfig = config.tech;
    }
  }
  return initialStoredTechConfig;
};

export const getSeriesId = (videoContent: Content) => {
  const type = videoContent.type;

  if (type === 'series') return;
  if (videoContent.series && (type === 'season_of_series' || type === 'episode')) {
    return videoContent.series.id;
  }
};

// VTT Thumbnail services
export const getVtt = async (vttUrl: string) => {
  return fetch(vttUrl)
    .then((res) => res.text())
    .then((data) => {
      return parseVtt(data);
    })
    .catch((e) => console.log(e));
};

function parseFrames(inputText: string): Frame[] {
  const lines = inputText.split('\n').filter((line) => line.trim() !== '');
  // Remove the "WEBVTT" line
  lines.shift();
  const imageDataArray: Frame[] = [];

  for (let i = 0; i < lines.length; i++) {
    if (i % 3 === 0) {
      const index = Math.floor(i / 3) + 1;
      const [startTime, endTime] = lines[i + 1].split(' --> ').map(timeToSeconds);
      const [imagePath, coordinates] = lines[i + 2].split('#xywh=');
      const [x, y, width, height] = coordinates.split(',').map(Number);

      const imageData: Frame = {
        index,
        startTime,
        endTime,
        x,
        y,
        width,
        height,
        imagePath,
      };

      imageDataArray.push(imageData);
    }
  }

  return imageDataArray;
}

const getThumbnails = (imageDataArray: Frame[]): ThumbData => {
  const imageDataObject: ThumbData = {};

  for (let i = 0; i < imageDataArray.length; i++) {
    imageDataObject[i] = imageDataArray[i];
  }

  return imageDataObject;
};

const parseVtt = (vttDataString: string): ThumbsData => {
  const frames = parseFrames(vttDataString);
  const thumbnailMap = getThumbnails(frames);
  const skipRate = 1;
  const firstFrame = frames[0];
  const interval = (firstFrame.endTime - firstFrame.startTime) * skipRate;
  const grid = getGrid(frames);
  const sprites = getSources(frames);

  return { thumbnailMap, interval, grid, sprites };
};

const getGrid = (frames: Frame[]) => {
  const firstImageFrames = frames.filter(({ imagePath }) => imagePath === frames[0].imagePath);
  const firstImageLastFrame = firstImageFrames[firstImageFrames.length - 1];
  const { x, y, width, height } = firstImageLastFrame;

  const cols = x / width + 1;
  const rows = y / height + 1;

  return { cols, rows };
};

const getSources = (frames: Frame[]) => Array.from(new Set(frames.map(({ imagePath }) => imagePath)));

export const findCaptionLabel = (key: string) => {
  const item = captionsKeys.find((item) => item.name === key || item.code === key);
  if (item) return item.label;
  return key;
};

const captionsKeys = [
  { code: 'pr', name: 'per', label: 'فارسی' },
  { code: 'fa', name: 'fa', label: 'فارسی' },
  { code: 'ab', name: 'abk', label: 'آبخازی' },
  { code: 'aa', name: 'aar', label: 'عفاری' },
  { code: 'af', name: 'afr', label: 'آفریکانس' },
  { code: 'ak', name: 'aka', label: 'آکان' },
  { code: 'sq', name: 'sqi', label: 'آلبانیایی' },
  { code: 'am', name: 'amh', label: 'امهری' },
  { code: 'ar', name: 'ara', label: 'عربی' },
  { code: 'an', name: 'arg', label: 'آراگونی' },
  { code: 'hy', name: 'hye', label: 'ارمنی' },
  { code: 'as', name: 'asm', label: 'آسامی' },
  { code: 'av', name: 'ava', label: 'آواری' },
  { code: 'ae', name: 'ave', label: 'اوستایی' },
  { code: 'ay', name: 'aym', label: 'آیمارا' },
  { code: 'az', name: 'aze', label: 'آذربایجانی' },
  { code: 'bm', name: 'bam', label: 'بامبارایی' },
  { code: 'ba', name: 'bak', label: 'باشقیری' },
  { code: 'eu', name: 'eus', label: 'باسکی' },
  { code: 'be', name: 'bel', label: 'بلاروسی' },
  { code: 'bn', name: 'ben', label: 'بنگالی' },
  { code: 'bi', name: 'bis', label: 'بیسلاما' },
  { code: 'bs', name: 'bos', label: 'بوسنیایی' },
  { code: 'br', name: 'bre', label: 'برتونی' },
  { code: 'bg', name: 'bul', label: 'بلغاری' },
  { code: 'my', name: 'mya', label: 'برمه‌ای' },
  { code: 'ca', name: 'cat', label: 'کاتالان، والنسین' },
  { code: 'ch', name: 'cha', label: 'چامورو' },
  { code: 'ce', name: 'che', label: 'چچنی' },
  { code: 'ny', name: 'nya', label: 'چیچوا، چهوا، نیانجا' },
  { code: 'zh', name: 'zho', label: 'چینی' },
  { code: 'cv', name: 'chv', label: 'چواشی' },
  { code: 'kw', name: 'cor', label: 'کرنیش' },
  { code: 'co', name: 'cos', label: 'کورسی' },
  { code: 'cr', name: 'cre', label: 'کری' },
  { code: 'hr', name: 'hrv', label: 'کرواتی' },
  { code: 'cs', name: 'ces', label: 'چکی' },
  { code: 'da', name: 'dan', label: 'دانمارکی' },
  { code: 'dv', name: 'div', label: 'دیوهی، دیویهی، مالدیوی' },
  { code: 'nl', name: 'nld', label: 'هلندی' },
  { code: 'dz', name: 'dzo', label: 'دزونگخا' },
  { code: 'en', name: 'eng', label: 'انگلیسی' },
  { code: 'eo', name: 'epo', label: 'اسپرانتو' },
  { code: 'et', name: 'est', label: 'استونیایی' },
  { code: 'ee', name: 'ewe', label: 'اوه' },
  { code: 'fo', name: 'fao', label: 'فارویی' },
  { code: 'fj', name: 'fij', label: 'فیجیایی' },
  { code: 'fi', name: 'fin', label: 'فنلاندی' },
  { code: 'fr', name: 'fra', label: 'فرانسوی' },
  { code: 'ff', name: 'ful', label: 'فولانی' },
  { code: 'gl', name: 'glg', label: 'گالیسیایی' },
  { code: 'ka', name: 'kat', label: 'گرجی' },
  { code: 'de', name: 'deu', label: 'آلمانی' },
  { code: 'el', name: 'ell', label: 'یونانی' },
  { code: 'gn', name: 'grn', label: 'گوارانی' },
  { code: 'gu', name: 'guj', label: 'گجراتی' },
  { code: 'ht', name: 'hat', label: 'هائیتی' },
  { code: 'ha', name: 'hau', label: 'هوسیایی' },
  { code: 'he', name: 'heb', label: 'عبری' },
  { code: 'hz', name: 'her', label: 'هررو' },
  { code: 'hi', name: 'hin', label: 'هندی' },
  { code: 'ho', name: 'hmo', label: 'هیری موتو' },
  { code: 'hu', name: 'hun', label: 'مجاری' },
  { code: 'ia', name: 'ina', label: 'اینترلینگوا' },
  { code: 'id', name: 'ind', label: 'اندونزیایی' },
  { code: 'ie', name: 'ile', label: 'اینترلینگ' },
  { code: 'ga', name: 'gle', label: 'ایرلندی' },
  { code: 'ig', name: 'ibo', label: 'ایگبو' },
  { code: 'ik', name: 'ipk', label: 'اینوپیاک' },
  { code: 'io', name: 'ido', label: 'ایدو' },
  { code: 'is', name: 'isl', label: 'ایسلندی' },
  { code: 'it', name: 'ita', label: 'ایتالیایی' },
  { code: 'iu', name: 'iku', label: 'اینوکتیتوت' },
  { code: 'ja', name: 'jpn', label: 'ژاپنی' },
  { code: 'jv', name: 'jav', label: 'جاوه‌ای' },
  { code: 'kl', name: 'kal', label: 'کالالیسوت، گرینلندی' },
  { code: 'kn', name: 'kan', label: 'کانادا' },
  { code: 'kr', name: 'kau', label: 'کانوری' },
  { code: 'ks', name: 'kas', label: 'کشمیری' },
  { code: 'kk', name: 'kaz', label: 'قزاقی' },
  { code: 'km', name: 'khm', label: 'خمری مرکزی' },
  { code: 'ki', name: 'kik', label: 'کیکویو، گیکویو' },
  { code: 'rw', name: 'kin', label: 'کینیارواندا' },
  { code: 'ky', name: 'kir', label: 'قرقیزی' },
  { code: 'kv', name: 'kom', label: 'کومی' },
  { code: 'kg', name: 'kon', label: 'کنگویی' },
  { code: 'ko', name: 'kor', label: 'کره‌ای' },
  { code: 'ku', name: 'kur', label: 'کردی' },
  { code: 'kj', name: 'kua', label: 'کوانیاما' },
  { code: 'la', name: 'lat', label: 'لاتین' },
  { code: 'lb', name: 'ltz', label: 'لوکزامبورگی' },
  { code: 'lg', name: 'lug', label: 'لوگاندا' },
  { code: 'li', name: 'lim', label: 'لیمبورگی' },
  { code: 'ln', name: 'lin', label: 'لینگالا' },
  { code: 'lo', name: 'lao', label: 'لائوسی' },
  { code: 'lt', name: 'lit', label: 'لیتوانیایی' },
  { code: 'lu', name: 'lub', label: 'لوبا-کاتانگا' },
  { code: 'lv', name: 'lav', label: 'لتونیایی' },
  { code: 'gv', name: 'glv', label: 'مانکس' },
  { code: 'mk', name: 'mkd', label: 'مقدونی' },
  { code: 'mg', name: 'mlg', label: 'مالاگاسی' },
  { code: 'ms', name: 'msa', label: 'مالایی' },
  { code: 'ml', name: 'mal', label: 'مالایالام' },
  { code: 'mt', name: 'mlt', label: 'مالتی' },
  { code: 'mi', name: 'mri', label: 'مائوری' },
  { code: 'mr', name: 'mar', label: 'مراتی' },
  { code: 'mh', name: 'mah', label: 'مارشالی' },
  { code: 'mn', name: 'mon', label: 'مغولی' },
  { code: 'na', name: 'nau', label: 'نائورو' },
  { code: 'nv', name: 'nav', label: 'ناواهو' },
  { code: 'nd', name: 'nde', label: 'شمالی' },
  { code: 'nr', name: 'nbl', label: 'جنوبی' },
  { code: 'oc', name: 'oci', label: 'اکسیتان' },
  { code: 'oj', name: 'oji', label: 'اوجیوا' },
  { code: 'cu', name: 'chu', label: 'اسلاوی' },
  { code: 'om', name: 'orm', label: 'اورومو' },
  { code: 'or', name: 'ori', label: 'اودیا' },
  { code: 'os', name: 'oss', label: 'آسی، آستین' },
  { code: 'pa', name: 'pan', label: 'پنجابی' },
  { code: 'pi', name: 'pli', label: 'پالی' },
  { code: 'fa', name: 'per', label: 'فارسی' },
  { code: 'pl', name: 'pol', label: 'لهستانی' },
  { code: 'ps', name: 'pus', label: 'پشتو' },
  { code: 'pt', name: 'por', label: 'پرتغالی' },
  { code: 'qu', name: 'que', label: 'کچوا' },
  { code: 'rm', name: 'roh', label: 'رومانش' },
  { code: 'rn', name: 'run', label: 'روندی' },
  { code: 'ro', name: 'ron', label: 'رومانیایی' },
  { code: 'ru', name: 'rus', label: 'روسی' },
  { code: 'sa', name: 'san', label: 'سانسکریت' },
  { code: 'sc', name: 'srd', label: 'ساردینیایی' },
  { code: 'sd', name: 'snd', label: 'سندی' },
  { code: 'se', name: 'sme', label: 'سامی شمالی' },
  { code: 'sm', name: 'smo', label: 'ساموآیی' },
  { code: 'sg', name: 'sag', label: 'سانگو' },
  { code: 'sr', name: 'srp', label: 'صربی' },
  { code: 'gd', name: 'gla', label: 'گالیک، گالیک اسکاتلندی' },
  { code: 'sn', name: 'sna', label: 'شونا' },
  { code: 'si', name: 'sin', label: 'سینهالا، سینهالی' },
  { code: 'sk', name: 'slk', label: 'اسلواکی' },
  { code: 'sl', name: 'slv', label: 'اسلوونیایی' },
  { code: 'so', name: 'som', label: 'سومالیایی' },
  { code: 'st', name: 'sot', label: 'سوتوی جنوبی' },
  { code: 'es', name: 'spa', label: 'اسپانیایی' },
  { code: 'su', name: 'sun', label: 'سوندانی' },
  { code: 'sw', name: 'swa', label: 'سواحیلی' },
  { code: 'ss', name: 'ssw', label: 'سواتی' },
  { code: 'sv', name: 'swe', label: 'سوئدی' },
  { code: 'ta', name: 'tam', label: 'تامیلی' },
  { code: 'te', name: 'tel', label: 'انگلیسی (مخصوص ناشنوایان)' },
  { code: 'tg', name: 'tgk', label: 'تاجیکی' },
  { code: 'th', name: 'tha', label: 'تایلندی' },
  { code: 'ti', name: 'tir', label: 'تیگرینیا' },
  { code: 'bo', name: 'bod', label: 'تبتی' },
  { code: 'tk', name: 'tuk', label: 'ترکمنی' },
  { code: 'tl', name: 'tgl', label: 'تاگالوگ' },
  { code: 'tn', name: 'tsn', label: 'تسوانا' },
  { code: 'to', name: 'ton', label: 'تونگایی (جزایر تونگا)' },
  { code: 'tr', name: 'tur', label: 'ترکی' },
  { code: 'ts', name: 'tso', label: 'تسونگا' },
  { code: 'tt', name: 'tat', label: 'فارسی (مخصوص ناشنوایان)' },
  { code: 'tw', name: 'twi', label: 'توی' },
  { code: 'ty', name: 'tah', label: 'تاهیتی' },
  { code: 'ug', name: 'uig', label: 'اویغوری' },
  { code: 'uk', name: 'ukr', label: 'اوکراینی' },
  { code: 'ur', name: 'urd', label: 'اردو' },
  { code: 'uz', name: 'uzb', label: 'ازبکی' },
  { code: 've', name: 'ven', label: 'وندایی' },
  { code: 'vi', name: 'vie', label: 'ویتنامی' },
  { code: 'vo', name: 'vol', label: 'ولاپوک' },
  { code: 'wa', name: 'wln', label: 'والونی' },
  { code: 'cy', name: 'cym', label: 'ولزی' },
  { code: 'wo', name: 'wol', label: 'ولوف' },
  { code: 'fy', name: 'fry', label: 'فریزی غربی' },
  { code: 'xh', name: 'xho', label: 'خوسایی' },
  { code: 'yi', name: 'yid', label: 'یدیش' },
  { code: 'yo', name: 'yor', label: 'یوروبا' },
  { code: 'za', name: 'zha', label: 'چوانگ، ژوانگ' },
  { code: 'zu', name: 'zul', label: 'زولو' },
  { code: 'mai', name: 'mai', label: 'زبان اصلی' },
  { code: 'no', name: 'nor', label: 'نروژی' },
];
